import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { LOOM_LOCAL_DEMO_PROJECT_ID } from "../../api/loomAuthoringAssistant";
import { AircraftArchitectureDemo } from "./AircraftArchitectureDemo";
import { AircraftSectionDesignLab } from "./AircraftSectionDesignLab";
import {
  aircraftSectionSceneBindingsMatch,
  requestAircraftSectionBindingSet,
  requestLocalAircraftSectionBootstrap,
  type AircraftSectionAcceptedSceneBinding,
  type AircraftSectionBindingSet,
  type AircraftSectionExtractionBinding,
} from "./aircraftSectionBindingApi";
import type { AircraftSectionTarget } from "./aircraftSectionExtractionApi";
import {
  LoomNativeHybridWorkbench,
  type LoomNativeWorkbenchRuntimeSnapshot,
} from "./LoomNativeHybridWorkbench";
import {
  LoomTaskWorkspace,
  type LoomControlSide,
  type LoomTask,
  type LoomTaskSelectionContext,
} from "./LoomTaskWorkspace";
import { PropulsorDemo } from "./PropulsorDemo";
import "./loom-build-workbench.css";

export interface LoomBuildWorkbenchProps {
  readonly branch: string;
  readonly persistedProject: boolean;
  readonly projectId: string;
  readonly projectName: string;
}

interface TaskRuntimeSnapshot extends LoomNativeWorkbenchRuntimeSnapshot {
  readonly legacyControlPoseRevision?: string | null;
  readonly selectedControlSide?: LoomControlSide | null;
  readonly selectedControlSurfaceId?: string | null;
}

function query(): URLSearchParams | null {
  return typeof window === "undefined" ? null : new URLSearchParams(window.location.search);
}

function requestedTask(): LoomTask | null {
  const value = query();
  const canonical = value?.get("loomTask");
  if (canonical === "details" || canonical === "propulsor" || canonical === "section") {
    return canonical;
  }
  if (value?.get("architectureDemo") === "1" || value?.get("buildView") === "assembly") {
    return "details";
  }
  if (value?.get("buildView") === "section-design") return "section";
  return value?.get("buildView") === "propulsor" ? "propulsor" : null;
}

function evidenceOnly(): boolean {
  return import.meta.env.DEV && query()?.get("architectureEvidence") === "1";
}

function recordTask(task: LoomTask | null): void {
  if (typeof window === "undefined") return;
  const next = new URL(window.location.href);
  next.searchParams.delete("architectureDemo");
  next.searchParams.delete("buildView");
  if (task) next.searchParams.set("loomTask", task);
  else next.searchParams.delete("loomTask");
  const changed = next.href !== window.location.href;
  if (changed) {
    window.history.replaceState(window.history.state, "", next);
    window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
  }
}

interface SectionBindingIdentity {
  readonly bodyId: string;
  readonly effectiveOwnershipId: string;
  readonly occurrenceId: string;
}

interface SelectedSectionBinding {
  readonly authoringNodeId: string;
  readonly binding: AircraftSectionExtractionBinding;
  readonly identity: SectionBindingIdentity;
  readonly targetKind: AircraftSectionTarget["kind"] | null;
}

function bindingIdentity(
  binding: Pick<AircraftSectionExtractionBinding, "occurrence" | "target">,
): SectionBindingIdentity {
  return { bodyId: binding.target.bodyId,
    effectiveOwnershipId: binding.occurrence.effectiveOwnershipId,
    occurrenceId: binding.occurrence.occurrenceId };
}

function sameBindingIdentity(
  left: SectionBindingIdentity,
  right: Pick<AircraftSectionExtractionBinding, "occurrence" | "target">,
): boolean {
  return left.occurrenceId === right.occurrence.occurrenceId
    && left.effectiveOwnershipId === right.occurrence.effectiveOwnershipId
    && left.bodyId === right.target.bodyId;
}

function resolveSelectionBinding(
  set: AircraftSectionBindingSet,
  selectedNodeId: string | null,
  selectedControlSurfaceId: string | null,
  selectedTargetKind: AircraftSectionTarget["kind"] | null,
): SelectedSectionBinding | null {
  if (selectedControlSurfaceId || !selectedNodeId || !selectedTargetKind) return null;
  const kindMatches = (binding: typeof set.entries[number]) => (
    binding.target.kind === selectedTargetKind
  );
  const body = set.entries.filter((binding) => (
    binding.target.bodyId === selectedNodeId
  ));
  const occurrence = set.entries.filter((binding) => (
    binding.occurrence.occurrenceId === selectedNodeId
  ));
  const owner = set.entries.filter((binding) => (
    binding.occurrence.effectiveOwnershipId === selectedNodeId
  ));
  const acceptedIdentity = ACCEPTED_SCENE_SECTION_IDENTITIES[
    selectedNodeId as keyof typeof ACCEPTED_SCENE_SECTION_IDENTITIES
  ];
  const accepted = acceptedIdentity ? set.entries.filter((binding) => (
    aircraftSectionSceneBindingsMatch(
      binding.acceptedSceneBinding, LOOM_NATIVE_HYBRID_ACCEPTED_SCENE_BINDING,
    ) && sameBindingIdentity(acceptedIdentity, binding)
  )) : [];
  let entry: typeof set.entries[number] | undefined;
  for (const identityCandidates of [body, occurrence, owner, accepted]) {
    if (!identityCandidates.length) continue;
    const candidates = identityCandidates.filter(kindMatches);
    if (candidates.length !== 1) return null;
    entry = candidates[0];
    break;
  }
  if (!entry) return null;
  const binding = {
    ...entry,
    aircraftVersionPin: set.aircraftVersionPin,
    poseStatePin: set.poseStatePin,
    projectId: set.projectId,
  };
  return { authoringNodeId: selectedNodeId, binding,
    identity: bindingIdentity(binding), targetKind: selectedTargetKind };
}

const ACCEPTED_SCENE_SECTION_IDENTITIES = {
  fuselage: {
    bodyId: "aircraft/loom-native-hybrid-v1/Body",
    effectiveOwnershipId: "aircraft/loom-native-hybrid-v1",
    occurrenceId: "loom-native-hybrid-aircraft",
  },
  wing: {
    bodyId: "aircraft/loom-native-hybrid-v1/Wing",
    effectiveOwnershipId: "aircraft/loom-native-hybrid-v1",
    occurrenceId: "loom-native-hybrid-aircraft",
  },
} as const;

const LOOM_NATIVE_HYBRID_ACCEPTED_SCENE_BINDING = {
  sourceDefinitionRevision: "sha256:cfc17eeb0c2530c8a037e671af66b3ebda1f4918506dc8ffb52773e0c6569431",
  acceptedGeometryRevision: "sha256:6cd3ded858259a4852a0d52d73a03c5a58646782018620c572d89d5af2a62366",
  acceptedTopologyRevision: "sha256:a69a1819fd67ff1561b60c34ab6d5d6c7429b7ea9caa29b2daf9555faffcb041",
  payloadRevision: "sha256:485c67cb2fdd18c2506b0cf57ccf6ba4ae297640afac09fa8736170c81925bd8",
  poseApplication: "identity-root/v1",
} as const satisfies AircraftSectionAcceptedSceneBinding;

function sectionBindingMatchesSelection(
  selected: SelectedSectionBinding,
  selectedNodeId: string | null,
  selectedControlSurfaceId: string | null,
  selectedTargetKind: AircraftSectionTarget["kind"] | null,
): boolean {
  if (selectedControlSurfaceId || !selectedNodeId) return false;
  return selected.authoringNodeId === selectedNodeId
    && selected.targetKind === selectedTargetKind
    && sameBindingIdentity(selected.identity, selected.binding);
}

function readableId(value: string | null | undefined): string {
  if (!value) return "Loading";
  return value.replace(/[._:/-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function LoomBuildWorkbench(props: LoomBuildWorkbenchProps) {
  const [activeTask, setActiveTask] = useState<LoomTask | null>(requestedTask);
  const [runtime, setRuntime] = useState<LoomNativeWorkbenchRuntimeSnapshot | null>(null);
  const [selectedSectionBinding, setSelectedSectionBinding] = useState<SelectedSectionBinding | null>(null);
  const [sectionPhase, setSectionPhase] = useState<"pending" | "ready" | "unavailable" | "error">("pending");
  const [sectionDiagnostic, setSectionDiagnostic] = useState<string | null>(null);
  const localBootstrapAttempted = useRef(false);
  const evidence = evidenceOnly();
  const taskRuntime = runtime as TaskRuntimeSnapshot | null;
  const selectedNodeId = taskRuntime?.selectedNodeId ?? null;
  const selectedControlSurfaceId = taskRuntime?.selectedControlSurfaceId ?? null;
  const selectedControlSide = taskRuntime?.selectedControlSide ?? null;
  const selectedSectionTargetKind = taskRuntime?.selectedSectionTargetKind ?? null;
  const sceneBinding = taskRuntime?.acceptedSceneBinding;
  const authorityProjectId = props.persistedProject ? props.projectId : LOOM_LOCAL_DEMO_PROJECT_ID;
  const currentSectionBinding = selectedSectionBinding?.binding.projectId === authorityProjectId
    && aircraftSectionSceneBindingsMatch(selectedSectionBinding.binding.acceptedSceneBinding, sceneBinding)
    && sectionBindingMatchesSelection(
      selectedSectionBinding, selectedNodeId, selectedControlSurfaceId, selectedSectionTargetKind,
    )
    ? selectedSectionBinding.binding : null;
  const visibleSectionPhase = !sceneBinding ? "unavailable"
    : sectionPhase === "ready" && !currentSectionBinding ? "unavailable" : sectionPhase;

  const openTask = useCallback((task: LoomTask) => setActiveTask(task), []);
  const closeTask = useCallback(() => setActiveTask(null), []);

  useEffect(() => {
    if (evidence) return undefined;
    recordTask(activeTask);
    const afterShellSync = window.setTimeout(() => recordTask(activeTask), 0);
    return () => window.clearTimeout(afterShellSync);
  }, [activeTask, evidence]);
  useEffect(() => {
    if (evidence) return undefined;
    const sync = () => setActiveTask(requestedTask());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, [evidence]);
  useEffect(() => { localBootstrapAttempted.current = false; }, [authorityProjectId]);
  useEffect(() => {
    if (evidence || activeTask !== "section") return undefined;
    if (!sceneBinding) {
      setSelectedSectionBinding(null);
      setSectionDiagnostic("This model is not ready for a section yet.");
      setSectionPhase("unavailable");
      return undefined;
    }
    if (selectedControlSurfaceId) {
      setSelectedSectionBinding(null);
      setSectionDiagnostic("Sections are not available for this control yet.");
      setSectionPhase("unavailable");
      return undefined;
    }
    if (currentSectionBinding) {
      setSectionDiagnostic(null);
      setSectionPhase("ready");
      return undefined;
    }
    const controller = new AbortController();
    setSelectedSectionBinding(null);
    setSectionDiagnostic(null);
    setSectionPhase("pending");
    const discover = async () => {
      try {
        return await requestAircraftSectionBindingSet(authorityProjectId, controller.signal);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "";
        const bootstrap = !props.persistedProject && import.meta.env.DEV
          && /API 404|not found/i.test(message) && !localBootstrapAttempted.current;
        if (!bootstrap) throw error;
        localBootstrapAttempted.current = true;
        await requestLocalAircraftSectionBootstrap(controller.signal);
        if (controller.signal.aborted) throw error;
        return requestAircraftSectionBindingSet(authorityProjectId, controller.signal);
      }
    };
    void discover().then((set) => {
      if (controller.signal.aborted) return;
      const selected = resolveSelectionBinding(
        set, selectedNodeId, selectedControlSurfaceId, selectedSectionTargetKind,
      );
      if (!selected) {
        setSectionDiagnostic("This component does not have section geometry yet.");
        setSectionPhase("unavailable");
        return;
      }
      if (!aircraftSectionSceneBindingsMatch(selected.binding.acceptedSceneBinding, sceneBinding)) {
        setSectionDiagnostic("The section source is out of date. Reload the model and try again.");
        setSectionPhase("unavailable");
        return;
      }
      setSelectedSectionBinding(selected);
      setSectionPhase("ready");
    }).catch(() => {
      if (controller.signal.aborted) return;
      setSectionDiagnostic("The section service is unavailable. The 3D model is unchanged.");
      setSectionPhase("error");
    });
    return () => controller.abort();
  }, [activeTask, authorityProjectId, currentSectionBinding, evidence,
    props.persistedProject, sceneBinding, selectedControlSurfaceId, selectedNodeId,
    selectedSectionTargetKind]);

  const selection = useMemo<LoomTaskSelectionContext>(() => ({
    controlSide: selectedControlSide,
    controlSurfaceId: selectedControlSurfaceId,
    nodeId: selectedNodeId,
    legacyControlPoseRevision: taskRuntime?.legacyControlPoseRevision ?? null,
    workspaceRevision: taskRuntime?.workspaceStateId ?? null,
  }), [selectedControlSide, selectedControlSurfaceId, selectedNodeId,
    taskRuntime?.legacyControlPoseRevision,
    taskRuntime?.workspaceStateId]);

  if (evidence) return (
    <section className="loom-build-evidence">
      <p data-testid="loom-build-evidence-boundary"><strong>Development evidence only.</strong> This prototype cannot change the active Loom workspace.</p>
      <AircraftArchitectureDemo branch={props.branch} projectName={props.projectName} />
    </section>
  );

  const details = (
    <div className="loom-task-details-content">
      <dl>
        <div><dt>Project</dt><dd>{props.projectName}</dd></div>
        <div><dt>Current version</dt><dd>r{taskRuntime?.authoringRevision ?? "—"}</dd></div>
        <div><dt>Selected part</dt><dd>{readableId(selectedControlSurfaceId ?? selectedNodeId)}</dd></div>
        <div><dt>3D scene</dt><dd>{taskRuntime ? "Ready" : "Loading"}</dd></div>
      </dl>
      <details data-technical-details data-testid="loom-technical-details">
        <summary>Technical details</summary>
        <dl>
          <div><dt>Accepted scene root</dt><dd><code>{taskRuntime?.acceptedSceneRootId ?? "pending"}</code></dd></div>
          <div><dt>Definition content state</dt><dd><code>{taskRuntime?.contentStateId ?? "pending"}</code></dd></div>
          <div><dt>Legacy control pose revision (pre-projection)</dt><dd><code>{selection.legacyControlPoseRevision ?? "unbound"}</code></dd></div>
          <div><dt>Workspace state</dt><dd><code>{selection.workspaceRevision ?? "pending"}</code></dd></div>
        </dl>
        <button className="loom-task-evidence-action" data-testid="loom-open-propulsor-evidence"
          onClick={() => openTask("propulsor")} type="button">Open propulsor evidence</button>
        <p className="loom-task-evidence-note">Adapted source evidence for this session only. Changes do not update the accepted aircraft or project history.</p>
      </details>
    </div>
  );
  const section = <section data-binding-phase={visibleSectionPhase}
    data-selected-node-id={selectedNodeId ?? "pending"}
    data-testid="aircraft-section-design-workbench">
    <AircraftSectionDesignLab
      accepted3dSignature={taskRuntime?.acceptedSceneRootId ?? `loom-scene:${authorityProjectId}`}
      binding={currentSectionBinding}
      bindingMessage={sectionDiagnostic}
      bindingPhase={visibleSectionPhase}
      taskDriven
    />
  </section>;

  return (
    <LoomTaskWorkspace
      activeTask={activeTask}
      details={details}
      onCloseTask={closeTask}
      propulsor={<PropulsorDemo taskDriven />}
      section={section}
      selection={selection}
    >
      <div className="loom-build-scene" data-project-id={props.projectId}>
        <LoomNativeHybridWorkbench {...props} onRuntimeSnapshot={setRuntime} onTaskRequest={openTask} />
      </div>
    </LoomTaskWorkspace>
  );
}

export default LoomBuildWorkbench;
